import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import { ExtendedProject } from "@/types/project";
import { getCurrentTenantId } from "@/utils/tenantUtils";
import { fieldTriggerService } from "@/services/fieldTriggerService";

// ============================================================================
// Types
// ============================================================================

export interface UseProjectFormOptions {
  project: ExtendedProject | null;
  projectId: string;
  userId?: string;
  /** Called before save starts - can be used for pre-save actions like tab switching */
  onBeforeSave?: () => void | Promise<void>;
  /** Called after successful save - can be used for post-save actions like setting flags */
  onAfterSave?: () => void | Promise<void>;
  /** Called when save succeeds */
  onSaveSuccess?: () => void;
  /** Called when save fails */
  onSaveError?: (error: Error) => void;
}

export interface ValidationErrors {
  [field: string]: string;
}

export interface UseProjectFormReturn {
  // State
  formValues: Partial<ExtendedProject>;
  originalFormValues: Partial<ExtendedProject>;
  formErrors: ValidationErrors;

  // Computed
  hasUnsavedChanges: boolean;
  isValid: boolean;
  saving: boolean;
  saveError: string | null;
  saveSuccess: boolean;
  dirtyFields: Set<string>;

  // Handlers
  handleInputChange: (
    arg:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
      | { name: string; value: unknown }
  ) => void;
  handleBulkChange: (updates: Partial<ExtendedProject>) => void;
  handleSaveOverviewChanges: () => Promise<void>;
  handleCancel: () => void;
  resetToOriginal: () => void;
  setFieldError: (field: string, error: string | undefined) => void;
  clearFieldErrors: (fields: string[]) => void;

  // Utilities
  getFieldError: (field: string) => string | undefined;
  hasFieldChanged: (field: string) => boolean;
  /** Log changes to activity_logs table (curried with projectId and userId) */
  logChangesToActivityLog: (updates: Record<string, unknown>) => Promise<void>;

  // Direct setters for advanced use cases
  setFormValues: React.Dispatch<React.SetStateAction<Partial<ExtendedProject>>>;
  setOriginalFormValues: React.Dispatch<React.SetStateAction<Partial<ExtendedProject>>>;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  setFormErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  setSaveError: React.Dispatch<React.SetStateAction<string | null>>;
  setSaveSuccess: React.Dispatch<React.SetStateAction<boolean>>;

  // Refs for external access
  originalFormValuesRef: React.MutableRefObject<Partial<ExtendedProject>>;
}

// ============================================================================
// Constants
// ============================================================================

// Fields that can be edited and saved in the overview/form tabs
export const EDITABLE_FIELDS = [
  // Contact Info
  'first_name', 'last_name', 'contact_email', 'contact_phone',
  'company_name', 'property_owner_name',

  // Project Address
  'project_street', 'project_city', 'project_state',
  'project_zip_code', 'project_county',

  // Property Details
  'hoa_name', 'hoa_phone', 'building_type', 'year_built',
  'panel_location', 'property_type', 'occupants', 'primary_residence',

  // System Details
  'system_cost', 'value', 'estimated_system_size', 'utility_provider', 'ahj',
  'project_type', 'system_type', 'mounting_type', 'module_manufacturer',
  'roof_type', 'roof_age', 'shading', 'mpuSize', 'storey',

  // Equipment Specifications
  'module_count', 'module_type', 'inverter_type', 'inverter_count',
  'battery_type', 'battery_count',

  // Adders (jsonb array of ProjectAdder snapshots — see addersService.ts)
  'adders',

  // Finance
  'financing', 'financing_option', 'finance_type',
  'profit_per_watt', 'total_project_profit',
  'allowed_commission_per_watt', 'total_commission',
  'material_cost', 'assigned_investor_id', 'financier_name',

  // Proposal inputs (PROP-G.2) — figures only a rep can collect, which the proposal binds and
  // PROP-G.4 derives offset and savings from. These REPLACE the phantom entries `electric_bill`
  // and `monthly_payment` that used to sit in the Finance group above: neither is a column on
  // `projects`, so both were unreachable, and leaving a dead synonym beside a live column is how
  // the next reader wires an input to the wrong one. (`value` and `financing_option` are phantoms
  // too — left alone here because they belong to the finance surface, not to this story. See the
  // note at the foot of migration 20260806i.)
  'annual_kwh_usage', 'monthly_electric_bill', 'solar_monthly_payment',
  'estimated_annual_production_kwh',

  // Commercial
  'commercial_type', 'entity_type', 'solar_trackers',
  'tax_benefit_eligible', 'grant_needed', 'payment_options',

  // Dates & Status
  'credit_application_status', 'contract_signed_date',
  'site_survey_completion_date', 'ntp_approved_date', 'ntp_status',
  'm1_submitted_date', 'm1_approved_date', 'm1_paid_date',
  'm2_submitted_date', 'm2_approved_date', 'm2_paid_date', 'installation_complete_date',
  'install_funding_received_date', 'pto_completed_date',
  'pto_received_manual_date',

  // Installer Payments
  'eighty_percent_installer_payment_paid',
  'eighty_percent_installer_payment_paid_date',
  'eighty_percent_installer_payment_paid_amount',
  'twenty_percent_installer_payment_paid',
  'twenty_percent_installer_payment_paid_date',
  'twenty_percent_installer_payment_paid_amount',
  'clawback_date',

  // Client-specific
  'language_preference', 'dg_rebate_required',

  // Other
  'sales_cycle', 'lead_source', 'rebates_applied', 'is_flipped_job',
  'open_external_cases', 'link_to_open_external_cases',
  'utility_bill_confirmed', 'drivers_licenses_confirmed',
  'project_notes',
] as const;

// Numeric fields that should convert empty strings to null
export const NUMERIC_FIELDS = [
  'profit_per_watt', 'total_project_profit', 'allowed_commission_per_watt',
  'total_commission', 'system_cost', 'value', 'estimated_system_size',
  'approved_grant_amount',
  'battery_size_kwh', 'desired_margin',
  'eighty_percent_installer_payment_paid_amount',
  'twenty_percent_installer_payment_paid_amount',
  'material_cost',
  'module_count', 'inverter_count', 'battery_count',
  // PROP-G.2. Must be here as well as in EDITABLE_FIELDS: a cleared numeric input sends `''`,
  // and Postgres answers 22P02 for the ENTIRE update, so one cleared field would discard every
  // other edit in the same save.
  'annual_kwh_usage', 'monthly_electric_bill', 'solar_monthly_payment',
  'estimated_annual_production_kwh',
] as const;

/**
 * The update payload for a project save: what changed, with cleared numerics as NULL.
 *
 * Extracted from `handleSaveOverviewChanges` (PROP-G.2) so the two allowlists above are
 * reachable from a test. Both fail silently and in opposite directions — a column missing from
 * EDITABLE_FIELDS is never written while the save still reports success, and a numeric column
 * missing from NUMERIC_FIELDS sends `''` and takes the whole update down with it — and neither
 * failure is visible from inside a `useCallback` that a node-only unit suite cannot call.
 *
 * Pure, and deliberately unaware of the metadata columns (`updated_at`, `last_updated_by_user`):
 * those are stamped by the caller, and including them here would make "did anything change?"
 * always true.
 */
export function buildProjectUpdates(
  formValues: Record<string, unknown>,
  originalFormValues: Record<string, unknown>,
): Record<string, unknown> {
  const updates: Record<string, unknown> = {};
  for (const field of EDITABLE_FIELDS) {
    if (formValues[field] !== originalFormValues[field]) {
      updates[field] = formValues[field];
    }
  }
  for (const field of NUMERIC_FIELDS) {
    // `field in updates` rather than a truthiness check: a legitimate 0 must survive, and
    // `updates[field] || null` would rewrite "0 kWh of usage" to "not collected".
    if (field in updates && (updates[field] === '' || updates[field] === null || updates[field] === undefined)) {
      updates[field] = null;
    }
  }
  return updates;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useProjectForm({
  project,
  projectId,
  userId,
  onBeforeSave,
  onAfterSave,
  onSaveSuccess,
  onSaveError,
}: UseProjectFormOptions): UseProjectFormReturn {
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  const [formValues, setFormValues] = useState<Partial<ExtendedProject>>({});
  const [originalFormValues, setOriginalFormValues] = useState<Partial<ExtendedProject>>({});
  const originalFormValuesRef = useRef<Partial<ExtendedProject>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<ValidationErrors>({});

  // -------------------------------------------------------------------------
  // Sync with project changes
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (project) {
      setFormValues(project);
      setOriginalFormValues(project);
      originalFormValuesRef.current = project;
      setHasUnsavedChanges(false);
      setFormErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]); // Reset only when project ID changes, not on every project object change

  // -------------------------------------------------------------------------
  // Computed values
  // -------------------------------------------------------------------------

  const isValid = useMemo(() => {
    return Object.keys(formErrors).every((key) => !formErrors[key]);
  }, [formErrors]);

  const dirtyFields = useMemo(() => {
    const dirty = new Set<string>();
    for (const field of EDITABLE_FIELDS) {
      if (formValues[field] !== originalFormValues[field]) {
        dirty.add(field);
      }
    }
    return dirty;
  }, [formValues, originalFormValues]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  /**
   * Handle input changes from form elements or direct value updates
   */
  const handleInputChange = useCallback((
    arg:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
      | { name: string; value: unknown }
  ) => {
    if ("target" in arg) {
      const { name, type } = arg.target;
      let value: string | boolean;
      if (type === "checkbox") {
        value = (arg.target as HTMLInputElement).checked;
      } else {
        value = (arg.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
      }
      setFormValues((prev) => {
        const newValues = { ...prev, [name]: value };
        setHasUnsavedChanges(
          JSON.stringify(newValues) !== JSON.stringify(originalFormValuesRef.current)
        );
        return newValues;
      });
    } else {
      const { name, value } = arg;
      setFormValues((prev) => {
        const newValues = { ...prev, [name]: value };
        setHasUnsavedChanges(
          JSON.stringify(newValues) !== JSON.stringify(originalFormValuesRef.current)
        );
        return newValues;
      });
    }
  }, []);

  /**
   * Handle bulk updates to multiple fields at once
   */
  const handleBulkChange = useCallback((updates: Partial<ExtendedProject>) => {
    setFormValues((prev) => {
      const newValues = { ...prev, ...updates };
      setHasUnsavedChanges(
        JSON.stringify(newValues) !== JSON.stringify(originalFormValuesRef.current)
      );
      return newValues;
    });

    // Clear errors for updated fields
    const fieldKeys = Object.keys(updates);
    if (fieldKeys.length > 0) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        let hasChanges = false;
        fieldKeys.forEach((field) => {
          if (newErrors[field]) {
            delete newErrors[field];
            hasChanges = true;
          }
        });
        return hasChanges ? newErrors : prev;
      });
    }
  }, []);

  /**
   * Save changes to the database
   */
  const handleSaveOverviewChanges = useCallback(async () => {
    if (!projectId || !hasUnsavedChanges) {
      return;
    }

    // Call onBeforeSave callback (e.g., for tab switching)
    if (onBeforeSave) {
      await onBeforeSave();
    }

    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      // Only what changed, with cleared numerics as NULL (see buildProjectUpdates).
      const updates = buildProjectUpdates(formValues, originalFormValues);

      // Update the project in database
      if (Object.keys(updates).length > 0) {
        updates.last_updated_by_user = userId || null;
        updates.updated_at = new Date().toISOString();

        const { error } = await supabase
          .from('projects')
          .update(updates)
          .eq('id', projectId);

        if (error) throw error;

        // Check field triggers for each updated field
        for (const [fieldName, newValue] of Object.entries(updates)) {
          // Skip metadata fields
          if (['last_updated_by_user', 'updated_at'].includes(fieldName)) continue;

          const oldValue = originalFormValues[fieldName as keyof typeof originalFormValues];

          // Only trigger if value actually changed
          if (fieldTriggerService.hasValueChanged(oldValue, newValue)) {
            fieldTriggerService.checkFieldTriggers({
              project_id: projectId,
              field_id: fieldName,
              field_name: fieldName,
              old_value: oldValue,
              new_value: newValue,
            }).catch(err => {
              console.error(`Field trigger check failed for ${fieldName}:`, err);
            });
          }
        }

        // Update local state
        setOriginalFormValues(formValues);
        originalFormValuesRef.current = formValues;
        setHasUnsavedChanges(false);

        // Log changes to activity log
        await logChangesToActivityLog(projectId, userId, updates);

        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);

        // Call onAfterSave callback (e.g., for setting recentlySaved flag)
        if (onAfterSave) {
          await onAfterSave();
        }

        onSaveSuccess?.();
      }
    } catch (error) {
      console.error("Error saving project:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to save changes";
      setSaveError(errorMessage);
      onSaveError?.(error instanceof Error ? error : new Error(errorMessage));
    } finally {
      setSaving(false);
    }
  }, [projectId, hasUnsavedChanges, formValues, originalFormValues, userId, onBeforeSave, onAfterSave, onSaveSuccess, onSaveError]);

  /**
   * Reset form to original values
   */
  const handleCancel = useCallback(() => {
    setFormValues(originalFormValues);
    setHasUnsavedChanges(false);
    setFormErrors({});
  }, [originalFormValues]);

  /**
   * Set error for a specific field
   */
  const setFieldError = useCallback((field: string, error: string | undefined) => {
    if (error === undefined) {
      // Remove the error
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    } else {
      setFormErrors((prev) => ({ ...prev, [field]: error }));
    }
  }, []);

  /**
   * Clear errors for multiple fields
   */
  const clearFieldErrors = useCallback((fields: string[]) => {
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      fields.forEach((field) => {
        delete newErrors[field];
      });
      return newErrors;
    });
  }, []);

  /**
   * Get error for a specific field
   */
  const getFieldError = useCallback((field: string) => {
    return formErrors[field];
  }, [formErrors]);

  /**
   * Check if a specific field has changed
   */
  const hasFieldChanged = useCallback((field: string) => {
    return dirtyFields.has(field);
  }, [dirtyFields]);

  /**
   * Curried version of logChangesToActivityLog with projectId and userId bound
   */
  const logActivityChanges = useCallback(async (updates: Record<string, unknown>) => {
    await logChangesToActivityLog(projectId, userId, updates);
  }, [projectId, userId]);

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  return {
    // State
    formValues,
    originalFormValues,
    formErrors,

    // Computed
    hasUnsavedChanges,
    isValid,
    saving,
    saveError,
    saveSuccess,
    dirtyFields,

    // Handlers
    handleInputChange,
    handleBulkChange,
    handleSaveOverviewChanges,
    handleCancel,
    resetToOriginal: handleCancel,
    setFieldError,
    clearFieldErrors,

    // Utilities
    getFieldError,
    hasFieldChanged,
    logChangesToActivityLog: logActivityChanges,

    // Direct setters
    setFormValues,
    setOriginalFormValues,
    setHasUnsavedChanges,
    setFormErrors,
    setSaving,
    setSaveError,
    setSaveSuccess,

    // Refs
    originalFormValuesRef,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Log changes to the activity_logs table
 */
async function logChangesToActivityLog(
  projectId: string,
  userId: string | undefined,
  updates: Record<string, unknown>
) {
  if (!userId || !projectId) {
    return;
  }

  // Fetch the current project data to compare with updates
  const { data: currentProject, error: fetchError } = await supabase
    .from("projects")
    .select("*")
    .eq("id", projectId)
    .single();

  if (fetchError) {
    console.error(
      "Error fetching current project data for change comparison:",
      fetchError
    );
    return;
  }

  // Categorize fields by type
  const contactFields = [
    "first_name", "last_name", "contact_email", "contact_phone", "company_name",
  ];
  const projectManagementFields = [
    "project_status", "assigned_sales_rep_id", "assigned_internal_pm_id",
    "assigned_installer_pm_id", "assigned_biz_dev_rep_id", "assigned_installer_partner_id",
  ];
  const checklistFields = ["checklist_status", "checklist_items"];

  // Extract changes by category
  const contactOld: Record<string, unknown> = {};
  const contactNew: Record<string, unknown> = {};
  const managementOld: Record<string, unknown> = {};
  const managementNew: Record<string, unknown> = {};
  const checklistOld: Record<string, unknown> = {};
  const checklistNew: Record<string, unknown> = {};
  const otherOld: Record<string, unknown> = {};
  const otherNew: Record<string, unknown> = {};

  // Compare each field and categorize changes
  Object.keys(updates).forEach((field) => {
    // Skip stage changes as they are handled separately
    if (field === "current_stage_id" || field === "stage_changed_at") {
      return;
    }

    const oldValue = currentProject[field];
    const newValue = updates[field];

    // Only log if there's a meaningful change
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      if (contactFields.includes(field)) {
        contactOld[field] = oldValue;
        contactNew[field] = newValue;
      } else if (projectManagementFields.includes(field)) {
        managementOld[field] = oldValue;
        managementNew[field] = newValue;
      } else if (checklistFields.includes(field)) {
        checklistOld[field] = oldValue;
        checklistNew[field] = newValue;
      } else {
        otherOld[field] = oldValue;
        otherNew[field] = newValue;
      }
    }
  });

  const tenantId = await getCurrentTenantId();

  // Log contact changes
  if (Object.keys(contactOld).length > 0) {
    await supabase.from("activity_logs").insert({
      project_id: projectId,
      user_id: userId,
      action_type: "project_updated",
      details: {
        field: "contact_info",
        old: contactOld,
        new: contactNew,
      },
      timestamp: new Date().toISOString(),
      related_entity_type: "contact",
      tenant_id: tenantId,
    });
  }

  // Log project management changes
  if (Object.keys(managementOld).length > 0) {
    await supabase.from("activity_logs").insert({
      project_id: projectId,
      user_id: userId,
      action_type: "project_updated",
      details: {
        field: "project_management",
        old: managementOld,
        new: managementNew,
      },
      timestamp: new Date().toISOString(),
      related_entity_type: "project",
      tenant_id: tenantId,
    });
  }

  // Log checklist changes
  if (Object.keys(checklistOld).length > 0) {
    await supabase.from("activity_logs").insert({
      project_id: projectId,
      user_id: userId,
      action_type: "project_updated",
      details: {
        field: "checklist",
        old: checklistOld,
        new: checklistNew,
      },
      timestamp: new Date().toISOString(),
      related_entity_type: "checklist",
      tenant_id: tenantId,
    });
  }

  // Log other changes
  if (Object.keys(otherOld).length > 0) {
    await supabase.from("activity_logs").insert({
      project_id: projectId,
      user_id: userId,
      action_type: "project_updated",
      details: {
        field: "other",
        old: otherOld,
        new: otherNew,
      },
      timestamp: new Date().toISOString(),
      related_entity_type: "project",
      tenant_id: tenantId,
    });
  }
}
